/**
 * The editing state of one score, as a React hook.
 *
 * Three things have to move together while a score is being edited -- the document with its
 * undo history, what is selected, and where the next note would go -- and keeping them in
 * three separate pieces of state is how they drift apart: a command deletes the note the
 * selection names, an undo puts back a bar the caret was standing in. They are held in one
 * value here and every action updates all three at once.
 *
 * Nothing is decided in this file. Every answer comes from the pure layers -- `commands`
 * for the document, `editor/selection`, `editor/entry` -- and what this adds is React: state
 * that survives a render, actions with stable identities, and the fact that a refusal is
 * remembered long enough to be shown rather than thrown away.
 */

import { useCallback, useMemo, useState } from "react";

import { measureOnset } from "../document/query";
import type { Score } from "../document/types";
import type { Command } from "../commands/types";
import {
  canRedo,
  canUndo,
  createSession,
  redo as redoSession,
  redoLabel,
  run as runSession,
  undo as undoSession,
  undoLabel,
} from "../commands/history";
import type { EditSession } from "../commands/history";
import { IDLE, respond } from "../editor/entry";
import type { Caret, EntryInput, EntryState } from "../editor/entry";
import { NOTHING, extend, focusLocation, prune, selectOne } from "../editor/selection";
import type { Selection } from "../editor/selection";
import type { Hit } from "../render/hit";

/** Everything a score-editing surface needs, and the ways it may change it. */
export interface Editor {
  readonly score: Score;
  readonly session: EditSession;
  readonly selection: Selection;
  readonly entry: EntryState;
  /** Why the last action did nothing, in words fit to show. Cleared by the next one. */
  readonly refusal: string | null;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  /** What undo and redo would do, for a menu item. */
  readonly undoLabel: string | null;
  readonly redoLabel: string | null;
  /** Run one command against the document. */
  run(command: Command, label?: string): void;
  /** Let the note-entry state machine answer for one thing the user did. */
  type(input: EntryInput): void;
  select(selection: Selection): void;
  /** Select what a click landed on, extending the selection when asked. */
  pick(hit: Hit | null, extending?: boolean): void;
  undo(): void;
  redo(): void;
  /** Start again on another document, with an empty history. */
  open(score: Score): void;
}

interface EditorState {
  readonly session: EditSession;
  readonly selection: Selection;
  readonly entry: EntryState;
  readonly refusal: string | null;
}

/** Where the caret stands when an event is selected: at that event, in its own voice. */
function caretFor(score: Score, selection: Selection): Caret | null {
  const at = focusLocation(score, selection);
  if (at === null) return null;
  return {
    partId: at.partId,
    measureId: at.measureId,
    voiceIndex: at.voiceIndex,
    onset: at.onset - measureOnset(score, at.measureIndex),
  };
}

/**
 * Move the caret to the selection.
 *
 * Placing a caret forgets the note last written, because after clicking somewhere else
 * there is nothing to tie from. A selected note is the exception: it is exactly the note a
 * chord tone should join, so it is put back as the last one.
 */
function placed(score: Score, state: EntryState, selection: Selection): EntryState {
  const caret = caretFor(score, selection);
  if (caret === null) return { ...state, caret: null, last: null, tie: false };
  const next = respond(score, state, { kind: "place", caret }).state;
  return { ...next, last: selection.focus };
}

/** Several commands as one, so that one keystroke is one undo step. */
function asOne(commands: readonly Command[], label: string): Command | null {
  const only = commands[0];
  if (only === undefined) return null;
  return commands.length === 1 ? only : { kind: "batch", label, commands };
}

const START: Omit<EditorState, "session"> = {
  selection: NOTHING,
  entry: IDLE,
  refusal: null,
};

/** An editor over `initial`, which is read once: after that the hook owns the document. */
export function useEditor(initial: Score, limit?: number): Editor {
  const [state, setState] = useState<EditorState>(() => ({
    ...START,
    session: createSession(initial, limit),
  }));

  const run = useCallback((command: Command, label?: string): void => {
    setState((current) => {
      const result = runSession(current.session, command, label);
      if (!result.ok) return { ...current, refusal: result.reason };
      const score = result.session.score;
      const selection = prune(score, current.selection);
      return {
        session: result.session,
        selection,
        entry: placed(score, current.entry, selection),
        refusal: null,
      };
    });
  }, []);

  const type = useCallback((input: EntryInput): void => {
    setState((current) => {
      const answer = respond(current.session.score, current.entry, input);
      const command = asOne(answer.commands, `type ${input.kind}`);
      if (command === null) {
        return { ...current, entry: answer.state, refusal: answer.refusal };
      }

      const result = runSession(current.session, command);
      if (!result.ok) return { ...current, refusal: result.reason };

      const written = answer.state.last;
      return {
        session: result.session,
        selection:
          written === null ? prune(result.session.score, current.selection) : selectOne(written),
        entry: answer.state,
        refusal: null,
      };
    });
  }, []);

  const select = useCallback((selection: Selection): void => {
    setState((current) => ({
      ...current,
      selection,
      entry: placed(current.session.score, current.entry, selection),
      refusal: null,
    }));
  }, []);

  const pick = useCallback((hit: Hit | null, extending = false): void => {
    setState((current) => {
      const score = current.session.score;
      const selection =
        hit === null
          ? NOTHING
          : extending
            ? extend(score, current.selection, hit.event, hit.pitchIndex)
            : selectOne(hit.event, hit.pitchIndex);
      return {
        ...current,
        selection,
        entry: placed(score, current.entry, selection),
        refusal: null,
      };
    });
  }, []);

  const step = useCallback(
    (move: (session: EditSession) => ReturnType<typeof undoSession>): void => {
      setState((current) => {
        const result = move(current.session);
        if (!result.ok) return { ...current, refusal: result.reason };
        const score = result.session.score;
        const selection = prune(score, current.selection);
        return {
          session: result.session,
          selection,
          entry: placed(score, current.entry, selection),
          refusal: null,
        };
      });
    },
    [],
  );

  const undo = useCallback((): void => step(undoSession), [step]);
  const redo = useCallback((): void => step(redoSession), [step]);

  const open = useCallback(
    (score: Score): void => {
      setState({ ...START, session: createSession(score, limit) });
    },
    [limit],
  );

  return useMemo(
    () => ({
      score: state.session.score,
      session: state.session,
      selection: state.selection,
      entry: state.entry,
      refusal: state.refusal,
      canUndo: canUndo(state.session),
      canRedo: canRedo(state.session),
      undoLabel: undoLabel(state.session),
      redoLabel: redoLabel(state.session),
      run,
      type,
      select,
      pick,
      undo,
      redo,
      open,
    }),
    [state, run, type, select, pick, undo, redo, open],
  );
}
